<template>
  <Modal ref="modalRef" @after-close="onAfterClose">
    <template #body>
      <View direction="column" gap="md" padding-block="md" class="confirmation">
        <ShowingIntentStep v-if="isIntentStep" v-model="intent" :renter-label="renterLabel" />

        <template v-else>
          <Text variant="h2" align="center">Please let the Renter know about the reason of showing decline</Text>

          <Text variant="body-large" color="secondary" align="center">
            The Renter will receive your explanation. After declining, Tallo will cease all communication and scheduling
            with this renter.
          </Text>

          <Textarea
            class="decline-reason-input"
            placeholder="Leave your feedback (optional)"
            rows="4"
            v-model="declineReasonInput"
          />

          <Hint v-if="fairHousingViolationReason" icon="alert-triangle" type="error">
            <View direction="column" gap="sm">
              <Text variant="label-medium">This may violate fair housing laws. Consider rephrasing it.</Text>
              <Text variant="body-medium">{{ fairHousingViolationReason }}</Text>
            </View>
          </Hint>

          <Hint v-if="!fairHousingViolationReason" icon="sparkle" type="info">
            <View direction="column" gap="sm">
              <Text variant="body-medium">
                Tallo automatically checks your response for fair housing compliance and will provide feedback if
                potential violations are found.
              </Text>
            </View>
          </Hint>
        </template>
      </View>
    </template>

    <template #footer>
      <template v-if="isIntentStep">
        <Button variant="ghost" @click="close">Cancel</Button>
        <Button :disabled="!intent" @click="continueWithIntent">Continue</Button>
      </template>

      <template v-else>
        <Button variant="ghost" @click="goBackToIntent">Back</Button>
        <Button :loading="isLoading" @click="declineShowing">Decline renter</Button>
      </template>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { AxiosError } from 'axios';
import { computed, ref } from 'vue';

import { Button, Modal, Textarea, Text, View, useAlertsStore, Hint } from '@tallo/design-system';

import { ShowingActionIntent } from '../../interfaces/showing-action-intent.enum';
import { useDeclineShowingRequest } from '../../mutations/decline-showing-request.mutation';
import ShowingIntentStep from '../showing-intent-step.vue';

const props = defineProps<{
  propertyId: string;
  showingRequestId: string;
  renterName?: string;
}>();

const emit = defineEmits<{ reschedule: [] }>();

const { mutateAsync, isLoading } = useDeclineShowingRequest();
const alertsStore = useAlertsStore();
const modalRef = ref<InstanceType<typeof Modal> | null>(null);
const declineReasonInput = ref('');
const fairHousingViolationReason = ref('');
const step = ref<'intent' | 'reason'>('intent');
const intent = ref<ShowingActionIntent | null>(null);
const isReschedulePending = ref(false);

const isIntentStep = computed(() => step.value === 'intent');
const renterLabel = computed(() => props.renterName?.split(' ')[0] || 'this renter');

function open() {
  fairHousingViolationReason.value = '';
  modalRef.value?.show();
}

function close() {
  modalRef.value?.close();
}

// Handing off to the reschedule modal is deferred until this one has finished closing,
// so the two modals never overlap and fight over the focus trap
function onAfterClose() {
  declineReasonInput.value = '';
  step.value = 'intent';
  intent.value = null;

  if (isReschedulePending.value) {
    isReschedulePending.value = false;
    emit('reschedule');
  }
}

function continueWithIntent() {
  if (intent.value === ShowingActionIntent.RESCHEDULE) {
    isReschedulePending.value = true;
    close();

    return;
  }

  step.value = 'reason';
}

function goBackToIntent() {
  fairHousingViolationReason.value = '';
  intent.value = null;
  step.value = 'intent';
}

function declineShowing() {
  mutateAsync({
    propertyId: props.propertyId,
    showingRequestId: props.showingRequestId,
    declineReason: declineReasonInput.value,
  })
    .then(() => {
      fairHousingViolationReason.value = '';
      close();
      alertsStore.success('Showing declined');
    })
    .catch((error) => {
      if (error instanceof AxiosError) {
        switch (error.response?.status) {
          case 406:
            fairHousingViolationReason.value = error?.response?.data?.message?.message;
            break;

          default:
            fairHousingViolationReason.value = '';
            alertsStore.catchError(error);
            break;
        }
      } else {
        fairHousingViolationReason.value = '';
        alertsStore.catchError(error);
      }
    });
}

defineExpose({ open });
</script>

<style scoped lang="scss">
.confirmation {
  max-width: 30rem;
}

.decline-reason-input {
  width: 100%;
}
</style>
